import { NavLink, Outlet, Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LOGO } from "@/lib/site";
import {
  LayoutDashboard, Building2, Newspaper, GraduationCap, Inbox,
  Handshake, Settings, FileText, LogOut, ExternalLink, Link2, Mail, Megaphone,
} from "lucide-react";

const GROUPS = [
  { title: "Overview", links: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  { title: "Content", links: [
    { to: "/admin/brokers", label: "Brokers", icon: Building2 },
    { to: "/admin/articles", label: "Articles", icon: Newspaper },
    { to: "/admin/education", label: "Education", icon: GraduationCap },
    { to: "/admin/legal", label: "Legal Content", icon: FileText },
  ] },
  { title: "Leads & Partners", links: [
    { to: "/admin/leads", label: "Leads", icon: Inbox },
    { to: "/admin/applications", label: "Partner Applications", icon: Handshake },
  ] },
  { title: "Marketing", links: [
    { to: "/admin/campaigns", label: "Campaigns", icon: Link2 },
    { to: "/admin/subscribers", label: "Subscribers", icon: Mail },
    { to: "/admin/promotions", label: "Promotions", icon: Megaphone },
  ] },
  { title: "System", links: [{ to: "/admin/settings", label: "Settings", icon: Settings }] },
];

const ALL_LINKS = GROUPS.flatMap((g) => g.links);

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-canvas text-subtle">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div dir="ltr" className="flex min-h-screen bg-canvas">
      <aside className="hidden w-64 shrink-0 flex-col bg-navy lg:flex">
        <Link to="/" className="flex items-center border-b border-white/10 px-6 py-5">
          <img src={LOGO} alt="MENAGroupFX" className="h-9 w-auto brightness-0 invert" />
        </Link>
        <nav className="flex-1 space-y-5 overflow-y-auto p-3">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{g.title}</p>
              <div className="space-y-1">
                {g.links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-finblue text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`
                    }
                    data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <l.icon className="h-5 w-5" strokeWidth={1.5} /> {l.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <a href="/" target="_blank" rel="noreferrer" className="mb-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:text-white"><ExternalLink className="h-4 w-4" /> View site</a>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-hair bg-white px-6 py-4 lg:hidden">
          <img src={LOGO} alt="MENAGroupFX" className="h-7 w-auto" />
          <Button size="sm" variant="ghost" onClick={() => { logout(); navigate("/admin/login"); }}><LogOut className="h-4 w-4" /></Button>
        </header>
        <div className="mb-16 flex gap-1 overflow-x-auto border-b border-hair bg-white px-2 lg:hidden">
          {ALL_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `whitespace-nowrap px-3 py-3 text-xs font-medium ${isActive ? "border-b-2 border-finblue text-finblue" : "text-subtle"}`}>{l.label}</NavLink>
          ))}
        </div>
        <main className="flex-1 overflow-y-auto p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
